import { forwardRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import { Box, BoxProps } from '@mui/material';
import { WatershedLinkProps } from '../types/LinkProps';
import isExternalUrl, { isApiRouteUrl } from '../utils/isExternalUrl';
import { format as formatUrl, type UrlObject } from 'url';
import { mixinSx } from '@watershed/style/styleUtils';
import type { NextjsLinkHref } from '@watershed/shared-universal/dashboardRoutes';

// Base props that are safe for both anchor and button elements
type BaseBoxLinkProps = Omit<
  BoxProps,
  'to' | 'tw' | 'component' | 'href' | 'type'
> &
  Omit<WatershedLinkProps, 'onClick' | 'tw'>;

// Props for when rendering as an anchor element (when href is present)
type AnchorBoxLinkProps = BaseBoxLinkProps & {
  href: string | UrlObject | NextjsLinkHref;
};

// Props for when rendering as a button element (when href is not present)
type DivBoxLinkProps = BaseBoxLinkProps & {
  href?: never;
};

export type BoxLinkProps = AnchorBoxLinkProps | DivBoxLinkProps;

export default forwardRef(function BoxLink(
  {
    href,
    children,
    replace,
    as,
    scroll,
    shallow,
    prefetch,
    locale,
    newTab,
    ...boxProps
  }: BoxLinkProps,
  ref
): JSX.Element {
  const hrefString = href ? formatUrl(href) : '';
  const isExternal = href !== undefined && isExternalUrl(hrefString);
  const isApiRoute = href !== undefined && isApiRouteUrl(hrefString);

  const anchorElement = (
    <Box
      {...(boxProps as React.ComponentPropsWithoutRef<'a'>)}
      {...(newTab ? { target: '_blank' } : {})}
      {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      component="a"
      href={hrefString}
      ref={ref}
      sx={mixinSx(boxProps.sx, {
        outlineColor: (theme) => theme.palette.primary.main,
      })}
    >
      {children}
    </Box>
  );

  if (href && !newTab && !isExternal && !isApiRoute) {
    return (
      <NextLink
        replace={replace}
        href={href}
        passHref
        as={as}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
        locale={locale}
        legacyBehavior
      >
        {anchorElement}
      </NextLink>
    );
  }

  if (href) {
    return anchorElement;
  }

  return (
    <Box
      {...(boxProps as React.ComponentPropsWithoutRef<'div'>)}
      component="div"
      role="button"
      ref={ref}
      sx={mixinSx(boxProps.sx, {
        outlineColor: (theme) => theme.palette.primary.main,
      })}
    >
      {children}
    </Box>
  );
});
